import React from 'react';
import { Card, CardContent, Box, Typography, IconButton } from '@mui/material';
import { Trash2 as TrashIcon, Edit2 as EditIcon } from 'lucide-react';

export const ExerciseCard = ({ exercise, onEdit, onDelete, showActions = true }) => {
  const getMuscleGroupColor = (group) => {
    const colors = {
      pecho: '#FF6B6B',
      espalda: '#4ECDC4',
      piernas: '#45B7D1',
      brazos: '#FFA07A',
      core: '#DDA15E',
    };
    return colors[group] || '#95E1D3';
  };

  return (
    <Card
      sx={{
        cursor: 'pointer',
        transition: 'all 0.2s',
        border: 'none',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        '&:hover': {
          boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
          transform: 'translateY(-2px)',
        },
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 0.5 }}>
              {exercise.name}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <Box
                sx={{
                  bgcolor: getMuscleGroupColor(exercise.muscleGroup),
                  color: 'white',
                  px: 1.5,
                  py: 0.5,
                  borderRadius: '20px',
                  fontSize: '0.75rem',
                  fontWeight: 'bold',
                }}
              >
                {exercise.muscleGroup}
              </Box>
              <Box
                sx={{
                  bgcolor: 'primary.main',
                  color: 'white',
                  px: 1.5,
                  py: 0.5,
                  borderRadius: '20px',
                  fontSize: '0.75rem',
                }}
              >
                {exercise.defaultSets}x{exercise.defaultReps}
              </Box>
            </Box>
            {exercise.defaultWeight > 0 && (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Peso: {exercise.defaultWeight} kg
              </Typography>
            )}
          </Box>
          {showActions && (
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {onEdit && (
                <IconButton size="small" onClick={onEdit}>
                  <EditIcon size={18} />
                </IconButton>
              )}
              {onDelete && (
                <IconButton size="small" onClick={onDelete} color="error">
                  <TrashIcon size={18} />
                </IconButton>
              )}
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export const RoutineCard = ({ routine, onStart, onEdit, onDelete, onDuplicate }) => {
  const isInactive = routine.active === false;
  return (
    <Card
      sx={{
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        transition: 'all 0.2s',
        opacity: isInactive ? 0.5 : 1,
        filter: isInactive ? 'grayscale(60%)' : 'none',
        '&:hover': {
          boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
        },
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              {routine.name}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {routine.days.length} días
            </Typography>
          </Box>
          {isInactive && (
            <Box
              sx={{
                px: 1,
                py: 0.25,
                bgcolor: 'grey.300',
                color: 'text.secondary',
                borderRadius: 1,
                fontSize: '0.7rem',
                fontWeight: 'bold',
                whiteSpace: 'nowrap',
              }}
            >
              Inactiva
            </Box>
          )}
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1, mb: 2 }}>
          {routine.days.map((day) => (
            <Box
              key={day.id}
              sx={{
                bgcolor: 'grey.100',
                p: 1,
                borderRadius: 1,
                textAlign: 'center',
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block' }}>
                {day.name}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {day.exercises.length} ejer.
              </Typography>
            </Box>
          ))}
        </Box>

        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', flexWrap: 'wrap' }}>
          {onStart && (
            <Box
              component="button"
              onClick={onStart}
              sx={{
                px: 2,
                py: 1,
                bgcolor: 'primary.main',
                color: 'white',
                border: 'none',
                borderRadius: 1,
                fontWeight: 'bold',
                cursor: 'pointer',
                fontSize: '0.85rem',
                transition: 'background 0.2s',
                '&:hover': { bgcolor: 'primary.dark' },
              }}
            >
              Entrenar
            </Box>
          )}
          {onDuplicate && (
            <Box
              component="button"
              onClick={onDuplicate}
              sx={{
                px: 1.5,
                py: 1,
                bgcolor: 'grey.200',
                border: 'none',
                borderRadius: 1,
                cursor: 'pointer',
                fontSize: '0.85rem',
              }}
            >
              📋
            </Box>
          )}
          {onEdit && (
            <Box
              component="button"
              onClick={onEdit}
              sx={{
                px: 1.5,
                py: 1,
                bgcolor: 'grey.200',
                border: 'none',
                borderRadius: 1,
                cursor: 'pointer',
                fontSize: '0.85rem',
              }}
            >
              ✏️
            </Box>
          )}
          {onDelete && (
            <Box
              component="button"
              onClick={onDelete}
              sx={{
                px: 1.5,
                py: 1,
                bgcolor: 'error.light',
                color: 'error.main',
                border: 'none',
                borderRadius: 1,
                cursor: 'pointer',
                fontSize: '0.85rem',
              }}
            >
              🗑️
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export const StatCard = ({ icon, label, value, variant = 'default' }) => {
  const bgColors = {
    success: 'success.light',
    warning: 'warning.light',
    error: 'error.light',
    info: 'info.light',
    default: 'primary.light',
  };

  return (
    <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
      <CardContent sx={{ textAlign: 'center', py: 3 }}>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 50,
            height: 50,
            borderRadius: '50%',
            bgcolor: bgColors[variant],
            mb: 1,
            fontSize: '1.5rem',
          }}
        >
          {icon}
        </Box>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
          {label}
        </Typography>
        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
          {value}
        </Typography>
      </CardContent>
    </Card>
  );
};
